import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { Toaster } from 'sonner'
import App from './app/App'
import { CookieConsentBanner } from './shared/components/CookieConsentBanner'
import { AuthProvider, ConfirmationProvider, LoadingProvider } from './shared/contexts'
import { queryClient } from './shared/lib/api'
import { resolveSentryEnv, shouldEnableSentry, isInjectedBrowserNoise } from './shared/lib/monitoring/sentryEnv'
import './shared/styles/globals.css'

const sentryEnv = resolveSentryEnv(import.meta.env.VITE_APP_ENV, window.location.hostname)

Sentry.init({
  dsn: 'https://d68ac1218a0bfc1f2fdcdc99d9edd835@o4509679859728384.ingest.us.sentry.io/4509679861170176',
  tunnel: 'https://sentry-proxy.vercel.app/api/sentry-tunnel/4509679861170176',
  enabled: shouldEnableSentry(sentryEnv),
  environment: sentryEnv,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/(develop)?professionals\.vercel\.app/,
    'https://sentry-proxy.vercel.app',
  ],
  ignoreErrors: [
    /Java object is gone/,
    /xbrowser is not defined/,
    /CookieConsentBannerPrototype is not defined/,
    /window\.webkit\.messageHandlers/,
  ],
  beforeSend(event) {
    return isInjectedBrowserNoise(event) ? null : event
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ConfirmationProvider>
          <LoadingProvider>
            <App />
            <CookieConsentBanner />
            <Toaster position="top-center" richColors />
            <ReactQueryDevtools initialIsOpen={false} />
          </LoadingProvider>
        </ConfirmationProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
